import { useMemo } from 'react'
import isEqual from 'lodash/isEqual'
import { add, subtract, vn } from 'vend-number'
import { useAppSelector } from '../../state/hooks'
import {
  selectLineItems,
  selectUnknownLineItems,
  selectSalePayments,
  selectSaleStatus,
  selectSaleId,
  selectOutstandingBalance,
  selectFulfilmentOutletNamesById,
} from '../../state/slices/currentSale/currentSaleSlice'
import { selectActiveOutletId } from '../../state/slices/posState/posStateSlice'
import { FulfilmentType } from '../../models/fulfilment'
import { LineItem, UnknownLineItem } from '../../models/line-item'
import { getPaymentsTotal, isOnAccount, isParked } from '../../functions/sale'
import { getFeatureFromCache } from '../../util/feature-cache'
import { FEATURE_FULFILLMENT_TAKE_NOW_ITEM_PAYMENT_ENFORCEMENT } from '../../constants'
import { useSaleHasServiceOrder } from './useSaleHasServiceOrder'

// Helper function to determine if a line item is a known product
// Unknown products do not have a price, so we need to check for that
const isKnownProduct = (
  lineItem: LineItem | UnknownLineItem
): lineItem is LineItem => 'fulfilmentType' in lineItem

export interface FulfilmentOutletGroup {
  outletId: string
  outletName: string
  isCurrentOutlet: boolean
  lines: LineItem[]
}

export interface FulfilmentOutletGroupsByType {
  delivery: FulfilmentOutletGroup[]
  pickup: FulfilmentOutletGroup[]
}

const sortOutletGroups = (groups: FulfilmentOutletGroup[]) =>
  groups.sort((a, b) => {
    if (a.isCurrentOutlet !== b.isCurrentOutlet) {
      return a.isCurrentOutlet ? -1 : 1
    }

    return a.outletName.localeCompare(b.outletName)
  })

const groupLinesByOutlet = ({
  lines,
  activeOutletId,
  fulfilmentOutletNamesById,
}: {
  lines: LineItem[]
  activeOutletId?: string
  fulfilmentOutletNamesById: Record<string, string>
}) => {
  const groupsByOutletId = new Map<string, FulfilmentOutletGroup>()

  lines.forEach(line => {
    const outletId = line.fulfilmentOutletId as string
    const group = groupsByOutletId.get(outletId)

    if (group) {
      group.lines.push(line)
      return
    }

    groupsByOutletId.set(outletId, {
      outletId,
      outletName: fulfilmentOutletNamesById[outletId],
      isCurrentOutlet: outletId === activeOutletId,
      lines: [line],
    })
  })

  return sortOutletGroups(Array.from(groupsByOutletId.values()))
}

/**
 * Hook to group sale line items by fulfillment type and calculate
 * payment-related balances for non-service fulfillment sales.
 *
 */
export const useFulfillmentTakeNowSaleData = () => {
  const lineItems = useAppSelector(selectLineItems, isEqual)
  const unknownLineItems = useAppSelector(selectUnknownLineItems, isEqual)
  const payments = useAppSelector(selectSalePayments)
  const saleStatus = useAppSelector(selectSaleStatus)
  const saleId = useAppSelector(selectSaleId)
  const isServiceSale = useSaleHasServiceOrder()
  const isOnAccountSale = isOnAccount(saleStatus)
  const outstandingBalance = useAppSelector(selectOutstandingBalance)
  const activeOutletId = useAppSelector(selectActiveOutletId)
  const fulfilmentOutletNamesById = useAppSelector(
    selectFulfilmentOutletNamesById,
    isEqual
  )
  const isTakeNowPaymentEnforcementFeatureEnabled = getFeatureFromCache(
    FEATURE_FULFILLMENT_TAKE_NOW_ITEM_PAYMENT_ENFORCEMENT
  )

  // A continued sale is one previously committed to the server (has an id),
  // but is NOT a parked sale.
  const isContinuedSale = Boolean(saleId) && !isParked(saleStatus)

  // Group line items by fulfillment type
  const { takeNowLines, pickupLaterLines, deliveryLines } = useMemo(() => {
    return [...lineItems, ...unknownLineItems]
      .sort((a, b) => a.dateAdded - b.dateAdded)
      .reduce<{
        takeNowLines: Array<LineItem | UnknownLineItem>
        pickupLaterLines: LineItem[]
        deliveryLines: LineItem[]
      }>(
        (acc, item) => {
          if (!isKnownProduct(item)) {
            acc.takeNowLines.push(item)
          } else if (item.fulfilmentType === FulfilmentType.PICKUP) {
            acc.pickupLaterLines.push(item)
          } else if (item.fulfilmentType === FulfilmentType.DELIVERY) {
            acc.deliveryLines.push(item)
          } else {
            acc.takeNowLines.push(item)
          }
          return acc
        },
        {
          takeNowLines: [],
          pickupLaterLines: [],
          deliveryLines: [],
        }
      )
  }, [lineItems, unknownLineItems])

  const fulfilmentOutletGroupsByType = useMemo<
    FulfilmentOutletGroupsByType | undefined
  >(() => {
    const fulfilmentLines = [...deliveryLines, ...pickupLaterLines]

    if (!fulfilmentLines.length) {
      return undefined
    }

    const hasAllOutletNames = fulfilmentLines.every(
      line =>
        line.fulfilmentOutletId &&
        Boolean(fulfilmentOutletNamesById[line.fulfilmentOutletId])
    )

    if (!hasAllOutletNames) {
      return undefined
    }

    return {
      delivery: groupLinesByOutlet({
        lines: deliveryLines,
        activeOutletId,
        fulfilmentOutletNamesById,
      }),
      pickup: groupLinesByOutlet({
        lines: pickupLaterLines,
        activeOutletId,
        fulfilmentOutletNamesById,
      }),
    }
  }, [
    activeOutletId,
    deliveryLines,
    fulfilmentOutletNamesById,
    pickupLaterLines,
  ])

  // Sum of inclusive prices for take now items (known products only)
  const takeNowItemsTotal = useMemo(() => {
    return takeNowLines.reduce<number>((total, item) => {
      if (isKnownProduct(item)) {
        // For both tax inclusive and tax exclusive stores, we need to subtract the sale discount from the inclusive price
        const itemGlobalDiscountPortion = add(
          item.totals.saleDiscount,
          item.totals.saleDiscountTax
        )
        return add(
          total,
          // Irrespective of whether the store is tax inclusive or exclusive, item.totals.inclusivePrice is the price of the item including tax
          subtract(item.totals.inclusivePrice, itemGlobalDiscountPortion)
        )
      }
      return total
    }, 0)
  }, [takeNowLines])

  // Total partial payments already made on this sale
  const totalPartialPayments = useMemo(() => {
    return getPaymentsTotal(payments)
  }, [payments])

  // How much is still outstanding for take now items after partial payments.
  // This should be rendered in the UI via formatCurrencyValue util
  const takeNowItemsBalance = useMemo(() => {
    return Math.max(0, subtract(takeNowItemsTotal, totalPartialPayments))
  }, [takeNowItemsTotal, totalPartialPayments])

  // Derived from remainingBalance so that when the sale is fully paid
  // (or overpaid), payLaterBalance correctly drops to zero.
  const payLaterBalance = useMemo(() => {
    return vn(outstandingBalance).lessThanOrEqualTo(0)
      ? 0
      : Math.max(0, subtract(outstandingBalance, takeNowItemsBalance))
  }, [outstandingBalance, takeNowItemsBalance])

  const hasFulfillmentLines =
    pickupLaterLines.length > 0 || deliveryLines.length > 0

  const hasTakeNowLines = takeNowLines.length > 0

  // True when the sale has fulfillment line items AND is NOT a service sale.
  const isNonServiceFulfillmentSale = hasFulfillmentLines && !isServiceSale

  // Custom logic, we only want to show breakdown of take now items owed when it makes sense
  // 1. When the sale is not on account -> Because on-account sales are always paid in full
  // 2. When the sale is a non-service fulfillment sale -> Because we only want to show breakdown for non-service fulfillment sales
  // 3. When the sale has take now lines -> Because we only want to show breakdown for sales that have take now lines
  // 4. When the take now items balance is greater than 0 -> Because we only want to show breakdown for sales that have take now items balance
  const shouldShowComplexSaleBalanceBreakdown =
    isTakeNowPaymentEnforcementFeatureEnabled &&
    !isOnAccountSale &&
    isNonServiceFulfillmentSale &&
    hasTakeNowLines &&
    vn(takeNowItemsBalance).round(2).greaterThan(0)

  // Custom logic to when show the grouping of sale-lines
  // 1. When it's a new sale (including saved sales) or a new on-account sale
  // 2. When the sale is a non-service fulfillment sale
  // 3. When the sale is not on account -> Because on-account sales are always paid in full
  const shouldShowGroupingOfSaleLines =
    isTakeNowPaymentEnforcementFeatureEnabled &&
    isNonServiceFulfillmentSale &&
    (!isContinuedSale || !isOnAccountSale)

  return {
    takeNowLines,
    pickupLaterLines,
    deliveryLines,
    fulfilmentOutletGroupsByType,
    takeNowItemsBalance,
    payLaterBalance: shouldShowComplexSaleBalanceBreakdown
      ? payLaterBalance
      : undefined,
    remainingBalance: outstandingBalance,
    shouldShowComplexSaleBalanceBreakdown,
    shouldShowGroupingOfSaleLines,
    isContinuedSale,
  }
}
